import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiUserCheck, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getInvitePreview, acceptInvite } from '../../services/api';
import { getApiErrorMessage } from '../../services/apiError';
import { useAuth } from '../../context/AuthContext';
import styles from './AcceptInvitePage.module.css';

const MODULE_LABELS = {
  receipts: 'Receipts',
  offerings: 'Offerings',
  payments: 'Payments',
  journalEntries: 'Journal Entries',
  members: 'Members & Families',
  donationCategories: 'Donation Categories',
  accountingSetup: 'Accounting Setup',
  reports: 'Reports',
  eventsNotices: 'Events & Notices',
  utils: 'Utilities',
};

const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(true);

  const [form, setForm] = useState({ name: '', phone: '', password: '', confirm: '' });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const data = await getInvitePreview(token);
        setPreview(data);
        if (data.name) {
          setForm((f) => ({ ...f, name: data.name }));
        }
      } catch (err) {
        setLoadError('This invitation is invalid or has expired.');
      } finally {
        setLoadingPreview(false);
      }
    };
    fetchPreview();
  }, [token]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    if (form.phone.trim().length < 7) { setFormError('Please enter a valid phone number.'); return; }
    if (form.password.length < 6) { setFormError('Password must be at least 6 characters.'); return; }
    if (form.password !== form.confirm) { setFormError('Passwords do not match.'); return; }

    setSubmitting(true);
    setFormError('');
    try {
      const response = await acceptInvite({ token, name: form.name.trim(), phone: form.phone.trim(), password: form.password });
      loginUser(response);
      toast.success('Welcome! Your account has been set up successfully.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to accept invitation'));
    } finally {
      setSubmitting(false);
    }
  };

  const grantedModules = preview
    ? Object.entries(preview.permissions || {}).filter(([, level]) => level !== 'none')
    : [];

  if (loadingPreview) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.iconBox}><FiUserCheck size={24} /></div>
          <h2 className={styles.title}>Invitation Not Found</h2>
          <p className={styles.desc}>{loadError}</p>
          <Link to="/login" className={styles.backLink}><FiArrowLeft size={13} /> Back to Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.iconBox}><FiUserCheck size={24} /></div>
        <h2 className={styles.title}>Accept Invitation</h2>
        <p className={styles.desc}>
          You have been invited to manage <strong>{preview.derasarName}</strong> as a co-admin.
          Set your name, phone number, and a password to get started.
        </p>

        <div className={styles.inviteInfo}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>{preview.email}</span>
          </div>
        </div>

        {grantedModules.length > 0 && (
          <div className={styles.permissionsBox}>
            <p className={styles.permissionsTitle}>Access granted</p>
            <ul className={styles.permissionsList}>
              {grantedModules.map(([mod, level]) => (
                <li key={mod} className={styles.permissionsItem}>
                  <span className={styles.modName}>{MODULE_LABELS[mod] || mod}</span>
                  <span className={styles.modLevel}>{level}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {formError && <div className={styles.errorMsg}>{formError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Your Name *</label>
            <input
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>
          <div className="form-group">
            <label>Phone Number *</label>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              placeholder="Enter your mobile number"
              required
            />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              placeholder="At least 6 characters"
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label>Confirm Password *</label>
            <input
              name="confirm"
              type="password"
              value={form.confirm}
              onChange={handleChange}
              placeholder="Re-enter password"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submitting}
            style={{ marginTop: 8 }}
          >
            {submitting ? 'Setting up account…' : 'Accept & Sign In'}
          </button>
        </form>

        <Link to="/login" className={styles.backLink}><FiArrowLeft size={13} /> Back to Sign In</Link>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
